<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { useAlert } from 'dashboard/composables';
import SalesRegistrationPermissionState from '../components/SalesRegistrationPermissionState.vue';
import SalesAICopyAlert from '../components/SalesAICopyAlert.vue';
import SalesPartyAddMenu from '../components/SalesPartyAddMenu.vue';
import SalesPartyList from '../components/SalesPartyList.vue';
import SalesRegistrationFooter from '../components/SalesRegistrationFooter.vue';
import { useSalesRegistration } from '../composables/useSalesRegistration';
import { useSalesParties } from '../composables/useSalesParties';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const transactionId = computed(() => route.params.salesTransactionId);
const role = computed(() => route.params.partyRole);
const selectedForRemoval = ref(null);
const parties = useSalesParties({ transactionId, role, t, notify: useAlert });
const registration = useSalesRegistration({
  id: transactionId,
  t,
  notify: useAlert,
});
const roleLabel = computed(() =>
  role.value === 'vendedor'
    ? t('SALES_PARTIES.ROLE.VENDEDOR')
    : t('SALES_PARTIES.ROLE.COMPRADOR')
);

const routeParams = () => ({
  accountId: route.params.accountId,
  salesTransactionId: transactionId.value,
  partyRole: role.value,
});
const goBack = () =>
  router.push({
    name: 'operacao_imobiliaria_vendas_cadastro',
    params: routeParams(),
  });
const add = partyKind =>
  router.push({
    name: 'operacao_imobiliaria_vendas_cadastro_parte_nova',
    params: { ...routeParams(), partyKind },
  });
const addRepresentative = company =>
  router.push({
    name: 'operacao_imobiliaria_vendas_cadastro_representante_novo',
    params: { ...routeParams(), parentPartyId: company.id },
  });
const edit = party =>
  router.push({
    name:
      party.relation_type === 'representante'
        ? 'operacao_imobiliaria_vendas_cadastro_representante'
        : 'operacao_imobiliaria_vendas_cadastro_parte',
    params: {
      ...routeParams(),
      partyKind: party.party_kind,
      partyId: party.id,
      parentPartyId: party.parent_party_id,
    },
  });
const confirmRemoval = async () => {
  if (await parties.remove(selectedForRemoval.value)) {
    selectedForRemoval.value = null;
    registration.load();
  }
};
const refresh = () => Promise.all([parties.load(), registration.load()]);
const goDelivery = () =>
  router.push({
    name: 'operacao_imobiliaria_vendas_entregar',
    params: {
      accountId: route.params.accountId,
      salesTransactionId: transactionId.value,
    },
  });

onMounted(parties.load);
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <SalesRegistrationPermissionState
      v-if="registration.isForbidden.value"
      @back="router.back()"
    />
    <template v-else>
      <LaisHeader
        :title="t('SALES_PARTIES.LIST.TITLE', { role: roleLabel })"
        :subtitle="t('SALES_PARTIES.LIST.SUBTITLE')"
        :breadcrumb="[
          { label: t('SALES.TITLE') },
          { label: t('SALES.REGISTRATION.TITLE') },
          { label: roleLabel },
        ]"
        show-back
        :back-label="t('SALES_PARTIES.ACTIONS.BACK')"
        @back="goBack"
      >
        <template #actions>
          <div class="flex flex-wrap gap-2">
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-refresh-cw"
              :is-loading="parties.loading.value || registration.loading.value"
              @click="refresh"
            >
              {{ t('SALES_PARTIES.ACTIONS.REFRESH') }}
            </LaisPrimaryButton>
            <SalesPartyAddMenu @add="add" />
            <SalesAICopyAlert
              :message="t('SALES_PARTIES.COMMON.AI_COPY_WARNING')"
            />
          </div>
        </template>
      </LaisHeader>

      <main class="mx-auto w-full max-w-[1500px] px-4 pb-32 sm:px-6 lg:px-10">
        <div
          v-if="parties.loading.value"
          class="rounded-lais border border-lais-border-soft bg-white p-8 text-center text-sm text-lais-text-muted"
        >
          {{ t('SALES_PARTIES.COMMON.LOADING') }}
        </div>
        <SalesPartyList
          v-else
          :people="parties.grouped.value.people"
          :companies="parties.grouped.value.companies"
          :role="role"
          @add="add"
          @add-representative="addRepresentative"
          @edit="edit"
          @remove="selectedForRemoval = $event"
        />
      </main>

      <SalesRegistrationFooter
        v-if="registration.transaction.value"
        :progress="registration.transaction.value.progress?.percent || 0"
        @deliver="goDelivery"
      />

      <LaisConfirmModal
        :show="Boolean(selectedForRemoval)"
        :title="t('SALES_PARTIES.REMOVE.TITLE')"
        :description="t('SALES_PARTIES.REMOVE.DESCRIPTION')"
        :confirm-text="t('SALES_PARTIES.REMOVE.CONFIRM')"
        :cancel-text="t('SALES_PARTIES.ACTIONS.CANCEL')"
        :is-loading="parties.saving.value"
        icon="i-lucide-trash-2"
        @close="selectedForRemoval = null"
        @confirm="confirmRemoval"
      />
    </template>
  </div>
</template>
